/**
 * CPP (Canada Pension Plan) Calculation Service
 *
 * Calculates monthly CPP benefits based on:
 * - Age at benefit start (60-70)
 * - Years of contributions (0-39)
 *
 * ## Official Rules (2025)
 *
 * - Maximum benefit at age 65: $1,433.85/month
 * - Early start penalty: 0.6% per month before 65 (max 36% at age 60)
 * - Late start bonus: 0.7% per month after 65 (max 42% at age 70)
 * - Pro-rating: Benefit * (yearsContributed / 39)
 *
 * ## References
 *
 * - [Government of Canada - CPP Benefits](https://www.canada.ca/en/services/benefits/publicpensions/cpp.html)
 * - [Service Canada - CPP Payment Amounts](https://www.canada.ca/en/services/benefits/publicpensions/cpp/cpp-benefit/amount.html)
 * - See docs/product/CANADIAN_RETIREMENT_RULES.md for detailed rules
 */

import BigNumber from "bignumber.js";

/**
 * CPP constants (2025 values)
 */
export const CPP_MAX_BENEFIT_2025 = 1433.85; // Monthly maximum at age 65
export const CPP_MIN_AGE = 60;
export const CPP_MAX_AGE = 70;
export const CPP_NORMAL_AGE = 65;
export const CPP_MAX_CONTRIBUTION_YEARS = 39; // Age 18-65
export const CPP_EARLY_PENALTY_RATE = 0.006; // 0.6% per month
export const CPP_LATE_BONUS_RATE = 0.007; // 0.7% per month

/**
 * Input parameters for CPP benefit calculation
 */
export interface CPPCalculationParams {
  age: number; // Current age (60-70, can be fractional)
  contributionYears: number; // Years contributed to CPP (0-39, can be fractional)
}

/**
 * Calculate monthly CPP benefit
 *
 * @param params - Calculation parameters
 * @returns Monthly CPP benefit amount in dollars
 * @throws Error if age is outside 60-70 range or contributionYears is negative
 *
 * @example
 * // Maximum benefit at normal retirement age
 * calculateCPPBenefit({ age: 65, contributionYears: 39 }); // 1433.85
 *
 * @example
 * // Early start with penalty
 * calculateCPPBenefit({ age: 60, contributionYears: 39 }); // 917.66 (36% reduction)
 *
 * @example
 * // Late start with bonus
 * calculateCPPBenefit({ age: 70, contributionYears: 39 }); // 2036.07 (42% increase)
 *
 * @example
 * // Pro-rated for incomplete contribution years
 * calculateCPPBenefit({ age: 65, contributionYears: 30 }); // 1102.65 (30/39 of max)
 */
export function calculateCPPBenefit(params: CPPCalculationParams): number {
  const { age, contributionYears } = params;

  // Validation: Age must be between 60 and 70
  if (age < CPP_MIN_AGE || age > CPP_MAX_AGE) {
    throw new Error(
      `CPP benefits can only start between ages ${CPP_MIN_AGE} and ${CPP_MAX_AGE}`
    );
  }

  // Validation: Contribution years cannot be negative
  if (contributionYears < 0) {
    throw new Error("Contribution years cannot be negative");
  }

  // Handle zero contributions
  if (contributionYears === 0) {
    return 0;
  }

  // Step 1: Cap contribution years at maximum (39)
  const effectiveContributionYears = Math.min(
    contributionYears,
    CPP_MAX_CONTRIBUTION_YEARS
  );

  // Step 2: Calculate pro-rated base benefit
  const proRatingFactor = new BigNumber(effectiveContributionYears).div(
    CPP_MAX_CONTRIBUTION_YEARS
  );
  const proRatedBenefit = new BigNumber(CPP_MAX_BENEFIT_2025)
    .times(proRatingFactor)
    .toNumber();

  // Step 3: Calculate age adjustment multiplier
  const monthsDifference = Math.round(
    new BigNumber(age).minus(CPP_NORMAL_AGE).times(12).toNumber()
  );

  let ageAdjustmentMultiplier: number;
  if (monthsDifference < 0) {
    // Early start: penalty of 0.6% per month
    const penaltyRate = Math.abs(monthsDifference) * CPP_EARLY_PENALTY_RATE;
    ageAdjustmentMultiplier = new BigNumber(1).minus(penaltyRate).toNumber();
  } else if (monthsDifference > 0) {
    // Late start: bonus of 0.7% per month
    const bonusRate = monthsDifference * CPP_LATE_BONUS_RATE;
    ageAdjustmentMultiplier = new BigNumber(1).plus(bonusRate).toNumber();
  } else {
    // Normal age 65: no adjustment
    ageAdjustmentMultiplier = 1;
  }

  // Step 4: Apply age adjustment and round to 2 decimals (cents)
  const finalBenefit = new BigNumber(proRatedBenefit).times(
    ageAdjustmentMultiplier
  );

  return parseFloat(finalBenefit.toFixed(2));
}
